import React, { useState } from "react";
import { BINS } from "./swachhataContent";

/*
 * Animated school-yard scene for the Swachhata Hi Seva survey popup: four
 * bins with faces catch the right waste as it falls in. Tap a bin to peek.
 *
 * mood: "idle" | "yes" (bins cheer) | "no" (litter blows in) | "party" (confetti)
 * Everything is SVG + CSS keyframes (SwachhataSurveyStage.css, prefix shsx-).
 */

const VIEW_W = 560;
const BIN_Y = 352;
const CX = [94, 218, 342, 466];
const LABEL = { wet: "WET", dry: "DRY", sanitary: "SANITARY", special: "SPECIAL CARE" };
const TIP = {
  wet: "Peels, leftover food, flowers, leaves",
  dry: "Paper, cardboard, plastic, glass, cans",
  sanitary: "Used diapers and napkins, wrapped",
  special: "Batteries, bulbs, paint, old medicines",
};
// seconds into the 6 s loop at which each bin catches its item, and where the item swings in from
const DROP = { wet: [0, -70], dry: [1.5, 60], special: [3, -55], sanitary: [4.5, 50] };

const BinIcon = ({ kind }) => {
  const p = { fill: "none", stroke: "#fff", strokeWidth: 2.4, strokeLinecap: "round", strokeLinejoin: "round" };
  if (kind === "wet")
    return (
      <g {...p}>
        <path d="M-9 7C-9-5 1-10 10-10C11-1 6 8-6 9Z" />
        <path d="M-9 7 3-3" />
      </g>
    );
  if (kind === "dry")
    return (
      <g {...p}>
        <path d="M-3-11h6v4l3 4v13q0 2-2 2h-8q-2 0-2-2V-3l3-4Z" />
        <path d="M-3 2h6" />
      </g>
    );
  if (kind === "sanitary")
    return (
      <g {...p}>
        <circle r="10" />
        <path d="M0-5v10M-5 0h10" />
      </g>
    );
  return (
    <g {...p}>
      <path d="M0-11 11 9H-11Z" />
      <path d="M0-3v5M0 5.5v.5" />
    </g>
  );
};

const Item = ({ kind }) => {
  if (kind === "wet")
    return (
      <g>
        <path d="M-16-2C-8 12 8 12 16-6 8 1 2 2-4 1-9 0-13-1-16-2Z" fill="#ffd23f" stroke="#c99a0a" strokeWidth="1.5" />
        <path d="M-4 1C-6 8-11 12-16 12-10 8-9 4-9 0Z" fill="#f5c400" stroke="#c99a0a" strokeWidth="1.5" />
        <path d="M14-5 18-9" stroke="#6b4a1c" strokeWidth="3" strokeLinecap="round" />
      </g>
    );
  if (kind === "dry")
    return (
      <g>
        <rect x="-4" y="-18" width="8" height="5" rx="1.5" fill="#1e6fd9" />
        <path d="M-4-13h8l4 6v18q0 4-4 4H-4q-4 0-4-4V-7Z" fill="#cfe9ff" stroke="#6aa6e0" strokeWidth="1.6" />
        <rect x="-8" y="-2" width="16" height="7" fill="#7cc0ff" />
      </g>
    );
  if (kind === "sanitary")
    return (
      <g>
        <rect x="-13" y="-10" width="26" height="20" rx="4" fill="#fde6ec" stroke="#e79ab0" strokeWidth="1.6" />
        <path d="M-13 0h26M0-10v20" stroke="#e79ab0" strokeWidth="2" />
      </g>
    );
  return (
    <g>
      <rect x="-4" y="-17" width="8" height="4" rx="1" fill="#9aa3ad" />
      <rect x="-9" y="-14" width="18" height="28" rx="3" fill="#2b2b2b" />
      <rect x="-9" y="-14" width="18" height="9" rx="3" fill="#f28c28" />
      <path d="M-3 4h6M0 1v6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </g>
  );
};

const Tip = ({ bin, i }) => {
  const x = Math.min(Math.max(CX[i], 134), VIEW_W - 134);
  return (
    <g transform={`translate(${x} ${BIN_Y - 16})`} pointerEvents="none">
      <g className="shsx-tip">
        <rect x="-126" y="-56" width="252" height="50" rx="12" fill="#fff" stroke={bin.hex} strokeWidth="2.5" />
        <path d={`M${CX[i] - x - 8} -7 l8 9 8-9Z`} fill="#fff" stroke={bin.hex} strokeWidth="2.5" strokeLinejoin="round" />
        <path d={`M${CX[i] - x - 7} -8.5h14`} stroke="#fff" strokeWidth="3" />
        <text x="0" y="-36" textAnchor="middle" className="shsx-tip__head" fill={bin.dark}>{`${bin.colour} · ${bin.name}`}</text>
        <text x="0" y="-18" textAnchor="middle" className="shsx-tip__body">{TIP[bin.key]}</text>
      </g>
    </g>
  );
};

const RAIN_COLOURS = ["#22a447", "#1e6fd9", "#d93a3a", "#2b2b2b", "#f28c28", "#ffd23f"];

export default function SwachhataSurveyScene({ mood = "idle", hint = true }) {
  const [open, setOpen] = useState(null);
  const toggle = (key) => setOpen((k) => (k === key ? null : key));

  return (
    <div className={`shsx-scene shsx-scene--${mood}`}>
      <svg className="shsx-svg" viewBox={`0 0 ${VIEW_W} 520`} role="img" aria-label="Four colour-coded bins catching waste in a school yard">
        <defs>
          <radialGradient id="shsx-glow">
            <stop offset="0%" stopColor="#fff3a8" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#fff3a8" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* sun */}
        <g transform="translate(470 126)">
          <circle r="70" fill="url(#shsx-glow)" />
          <g className="shsx-rays">
            {Array.from({ length: 12 }, (_, k) => (
              <rect key={k} x="-3" y="-52" width="6" height="14" rx="3" fill="#ffc933" transform={`rotate(${k * 30})`} />
            ))}
          </g>
          <circle r="30" fill="#ffd23f" />
          <circle cx="-10" cy="-4" r="3" fill="#7a4a00" />
          <circle cx="10" cy="-4" r="3" fill="#7a4a00" />
          <path className="shsx-sun-smile" d="M-11 7Q0 17 11 7" fill="none" stroke="#7a4a00" strokeWidth="3" strokeLinecap="round" />
        </g>

        {/* clouds */}
        {[
          [62, 1, "0s", "46s"],
          [150, 0.75, "-24s", "60s"],
          [34, 0.55, "-38s", "52s"],
        ].map(([y, s, delay, dur], k) => (
          <g key={k} transform={`translate(0 ${y})`}>
            <g className="shsx-cloud" style={{ animationDelay: delay, animationDuration: dur }}>
              <g transform={`scale(${s})`} fill="#fff">
                <ellipse cx="40" cy="20" rx="40" ry="18" />
                <ellipse cx="70" cy="8" rx="30" ry="22" />
                <ellipse cx="100" cy="22" rx="32" ry="16" />
              </g>
            </g>
          </g>
        ))}

        {/* hills */}
        <path d="M0 322Q90 262 196 304T392 296T560 282V520H0Z" fill="#c4e8ab" />
        <path d="M0 372Q140 344 290 368T560 352V520H0Z" fill="#96d377" />

        {/* school */}
        <g transform="translate(40 232)">
          <rect x="0" y="42" width="180" height="116" rx="4" fill="#fff4dc" />
          <path d="M-12 46 90 0 192 46Z" fill="#e2663b" />
          <rect x="54" y="54" width="72" height="17" rx="3" fill="#157a3c" />
          <text x="90" y="66.5" textAnchor="middle" className="shsx-sign">SCHOOL</text>
          {[14, 136].map((x) => (
            <g key={x}>
              <rect x={x} y="80" width="30" height="26" rx="3" fill="#9fd3ff" stroke="#c9a46a" strokeWidth="3" />
              <path d={`M${x + 15} 80v26M${x} 93h30`} stroke="#c9a46a" strokeWidth="2" />
            </g>
          ))}
          <rect x="76" y="100" width="28" height="58" rx="3" fill="#8a5a3b" />
          <line x1="90" y1="0" x2="90" y2="-52" stroke="#6b6b6b" strokeWidth="3" />
          <g transform="translate(91 -52)">
            <g className="shsx-flag-cloth">
              <rect width="36" height="8" fill="#ff9933" />
              <rect y="8" width="36" height="8" fill="#fff" />
              <rect y="16" width="36" height="8" fill="#138808" />
              <circle cx="18" cy="12" r="3" fill="none" stroke="#1a3fa0" strokeWidth="1.2" />
            </g>
          </g>
        </g>

        {/* trees */}
        {[
          [300, 296, 1],
          [398, 316, 0.8],
          [530, 300, 0.9],
        ].map(([x, y, s], k) => (
          <g key={k} transform={`translate(${x} ${y}) scale(${s})`}>
            <rect x="-5" y="10" width="10" height="46" rx="3" fill="#8a5a3b" />
            <g className="shsx-tree" style={{ animationDelay: `${-k * 0.9}s` }}>
              <circle cx="0" cy="0" r="30" fill="#3fa34d" />
              <circle cx="-18" cy="12" r="20" fill="#4fb659" />
              <circle cx="18" cy="10" r="22" fill="#4fb659" />
            </g>
          </g>
        ))}

        <path d="M0 446Q280 424 560 446V520H0Z" fill="#74c25a" />
        {[40, 170, 260, 390, 520].map((x, k) => (
          <g key={x} transform={`translate(${x} ${498 + (k % 2) * 10})`}>
            <circle r="3.5" fill={["#fff", "#ffd23f", "#ff9fb8"][k % 3]} />
          </g>
        ))}

        {/* drifting leaves */}
        {[0, 1, 2, 3].map((k) => (
          <g key={k} transform={`translate(${120 + k * 130} 0)`}>
            <path className="shsx-leaf" style={{ animationDelay: `${-k * 2.6}s` }} d="M-7 4C-7-4 0-8 7-8 8-1 4 6-4 7Z" fill={k % 2 ? "#5cbf5a" : "#9bd66b"} />
          </g>
        ))}

        <rect className="shsx-gloom" width={VIEW_W} height="520" fill="#58646b" />

        {/* falling items (drawn before the bins so the bins swallow them) */}
        {BINS.map((b, i) => (
          <g key={b.key} transform={`translate(${CX[i]} 0)`}>
            <g className="shsx-drop" style={{ "--d": `${DROP[b.key][0]}s`, "--x": `${DROP[b.key][1]}px` }}>
              <Item kind={b.key} />
            </g>
          </g>
        ))}

        {/* bins */}
        {BINS.map((b, i) => (
          <g key={b.key} transform={`translate(${CX[i] - 46} ${BIN_Y})`}>
            <ellipse cx="46" cy="138" rx="50" ry="8" fill="rgba(0,0,0,0.16)" />
            <g className="shsx-cheer" style={{ "--c": `${i * 0.14}s` }}>
              <g
                className={`shsx-bin${open === b.key ? " is-open" : ""}`}
                style={{ "--d": `${DROP[b.key][0]}s`, "--b": `${1.1 * i}s` }}
                role="button"
                tabIndex={0}
                aria-label={`${b.colour}: ${b.name}. ${TIP[b.key]}`}
                onClick={() => toggle(b.key)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  toggle(b.key);
                }}
              >
                <g className="shsx-gulp">
                  <path className="shsx-body" d="M2 16H90L82 126Q81 132 75 132H17Q11 132 10 126Z" fill={b.hex} />
                  <path d="M12 22H24L22 124H18Z" fill="rgba(255,255,255,0.18)" />
                  <circle cx="24" cy="66" r="6" fill="#ff8fa8" opacity="0.45" />
                  <circle cx="68" cy="66" r="6" fill="#ff8fa8" opacity="0.45" />
                  <g className="shsx-eyes">
                    <circle cx="33" cy="50" r="8" fill="#fff" />
                    <circle cx="59" cy="50" r="8" fill="#fff" />
                    <circle className="shsx-pupil" cx="34" cy="51" r="4" fill="#1b1b1b" />
                    <circle className="shsx-pupil" cx="60" cy="51" r="4" fill="#1b1b1b" />
                  </g>
                  <path className="shsx-mouth shsx-mouth--smile" d="M37 66Q46 74 55 66" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                  <path className="shsx-mouth shsx-mouth--grin" d="M33 63Q46 82 59 63Z" fill="#6d1616" stroke="#fff" strokeWidth="2.5" strokeLinejoin="round" />
                  <path className="shsx-mouth shsx-mouth--worry" d="M37 72Q46 63 55 72" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                  <g transform="translate(46 92)">
                    <BinIcon kind={b.key} />
                  </g>
                  <rect x="11" y="108" width="70" height="15" rx="4" fill="rgba(0,0,0,0.24)" />
                  <text x="46" y="118.8" textAnchor="middle" className="shsx-bin-label">{LABEL[b.key]}</text>
                  <circle cx="20" cy="134" r="6" fill="#333" />
                  <circle cx="72" cy="134" r="6" fill="#333" />
                  <g className="shsx-lid">
                    <rect x="-3" y="2" width="98" height="16" rx="6" fill={b.dark} />
                    <rect x="34" y="-5" width="24" height="9" rx="4" fill={b.dark} />
                  </g>
                </g>
                <g transform="translate(46 -14)">
                  <path className="shsx-spark" d="M0-14 4-4 14 0 4 4 0 14-4 4-14 0-4-4Z" fill="#ffd23f" />
                </g>
              </g>
            </g>
          </g>
        ))}

        {/* litter, only when the school says No */}
        <g className="shsx-litter" aria-hidden="true">
          <g transform="translate(0 440)">
            <g className="shsx-bag">
              <path d="M-14-14Q-18 4-10 16H10Q18 4 14-14L8-8 4-18-4-8-10-18Z" fill="#eef1f3" stroke="#b7c0c6" strokeWidth="1.6" />
            </g>
          </g>
          <g transform="translate(262 494) rotate(-20)">
            <rect x="-7" y="-10" width="14" height="20" rx="3" fill="#d93a3a" />
            <path d="M-7-2h14" stroke="#fff" strokeWidth="2" />
          </g>
          <circle cx="160" cy="500" r="7" fill="#f3efe4" stroke="#c9c2b0" strokeWidth="1.5" />
          <path d="M398 502l14-4 4 8-12 3Z" fill="#c9e7ff" stroke="#8ab8de" strokeWidth="1.4" />
        </g>

        {open && <Tip bin={BINS.find((b) => b.key === open)} i={BINS.findIndex((b) => b.key === open)} />}
      </svg>

      {hint && (
        <span className="shsx-hint">
          <i aria-hidden="true" />
          Tap a bin to see what goes in it
        </span>
      )}

      {mood === "party" && (
        <div className="shsx-rain" aria-hidden="true">
          {Array.from({ length: 34 }, (_, k) => (
            <span
              key={k}
              style={{
                "--l": `${(k * 37) % 100}%`,
                "--dl": `${-((k * 0.29) % 3.4)}s`,
                "--du": `${2.8 + (k % 5) * 0.45}s`,
                "--cl": RAIN_COLOURS[k % RAIN_COLOURS.length],
                "--w": `${6 + (k % 3) * 3}px`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const BURST_COLOURS = RAIN_COLOURS;
